import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Observable, combineLatest, interval, EMPTY, Subject, from } from 'rxjs';
import { map, takeUntil, switchMap, distinctUntilChanged, startWith, shareReplay } from 'rxjs/operators';
import { UserInfo } from '@memberjunction/core';
import { ActiveUserService, ActiveUserInfo } from '../../../services/active-user.service';
import { ImpersonationService, ImpersonationSession, SessionStatus } from '../../../services/impersonation.service';
import { EntityNames, SYSTEM_ROLES } from 'src/app/utils/constants';
import { UserRoleEntity } from '@memberjunction/core-entities';
import { DataFetchService } from 'src/app/services/data-fetch.service';

interface TimerState {
  remainingMs: number;
  totalMs: number;
}

interface SessionInfo {
  sessionId: string;
  targetUserRole: string;
  startTime: Date;
  expirationTime: Date;
  durationMinutes: number;
}

@Component({
  selector: 'acs-impersonation-banner',
  templateUrl: './impersonation-banner.component.html',
  styleUrls: ['./impersonation-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  @Input() isNavCollapsed: boolean = false;
  
  private readonly destroy$ = new Subject<void>();
  
  // Observable streams
  public readonly isImpersonating$: Observable<boolean>;
  public readonly targetUser$: Observable<UserInfo | null>;
  public readonly originalUser$: Observable<UserInfo | null>;
  public readonly sessionInfo$: Observable<SessionInfo | null>;
  public readonly timeRemaining$: Observable<TimerState | null>;
  public readonly isSessionExpiringSoon$: Observable<boolean>;

  // Component state
  public isExtending = false;
  public isEnding = false;

  private static readonly WARNING_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
  private static readonly TIMER_INTERVAL_MS = 1000; // 1 second

  constructor(
    private activeUserService: ActiveUserService,
    private impersonationService: ImpersonationService,
    private cdr: ChangeDetectorRef,
    private dataFetchService: DataFetchService
  ) {
    // Set up reactive streams
    this.isImpersonating$ = this.impersonationService.getSessionStatus().pipe(
      map(status => status.isImpersonating)
    );
    
    this.targetUser$ = this.activeUserService.activeUser$.pipe(
      map((activeUserInfo: ActiveUserInfo | null) => activeUserInfo?.activeUser || null)
    );
    
    this.originalUser$ = this.impersonationService.getSessionStatus().pipe(
      map(status => status.session?.originalUser || null)
    );
    
    this.sessionInfo$ = this.impersonationService.getSessionStatus().pipe(
      switchMap(status => from(this.mapSessionStatusAsync(status))),
      shareReplay(1)
    );

    // Set up timer stream that updates every second when impersonating
    this.timeRemaining$ = this.isImpersonating$.pipe(
      switchMap(isImpersonating => 
        isImpersonating 
          ? interval(ImpersonationBannerComponent.TIMER_INTERVAL_MS).pipe(
              startWith(0),
              switchMap(() => this.sessionInfo$),
              map(session => this.calculateTimeRemaining(session)),
              distinctUntilChanged((prev, curr) => 
                prev?.remainingMs === curr?.remainingMs
              )
            )
          : EMPTY
      )
    );

    // Set up expiration warning stream
    this.isSessionExpiringSoon$ = this.timeRemaining$.pipe(
      map(timer => 
        timer !== null && 
        timer.remainingMs > 0 && 
        timer.remainingMs <= ImpersonationBannerComponent.WARNING_THRESHOLD_MS
      ),
      distinctUntilChanged()
    );
  }

  ngOnInit(): void {
    // Subscribe to timer updates to trigger change detection
    this.timeRemaining$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.cdr.markForCheck();
    });

    // Subscribe to session expiration warnings
    this.isSessionExpiringSoon$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isExpiring => {
      if (isExpiring) {
        console.warn('Impersonation session expires soon');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Maps SessionStatus to our SessionInfo interface (async version)
   */
  private async mapSessionStatusAsync(status: SessionStatus): Promise<SessionInfo | null> {
    if (!status.isImpersonating || !status.session) {
      return null;
    }

    const session = status.session;
    const durationMs = session.expiresAt.getTime() - session.startedAt.getTime();
    const targetUserRole = await this.getUserRolesString(session.impersonatedUser);
    
    return {
      sessionId: session.id,
      targetUserRole,
      startTime: session.startedAt,
      expirationTime: session.expiresAt,
      durationMinutes: Math.round(durationMs / (60 * 1000))
    };
  }

  /**
   * Gets user roles as a string
   */
  private async getUserRolesString(user: UserInfo): Promise<string> {
    if (!user.UserRoles || user.UserRoles.length === 0) {
      const result = await this.dataFetchService.fetchWithParams<UserRoleEntity>({
        EntityName: EntityNames.UserRoles,
        ExtraFilter: `UserID = '${user.ID}'`,
        skipActiveFilter: true,
        ResultType: 'simple'
      });

      if (result.success && result.data && result.data.length > 0) {
        // Extract role names from the user roles
        const roleNames = result.data
          .map((ur: any) => ur.Role)
          .filter((roleName: string) => roleName && !SYSTEM_ROLES.includes(roleName));
        
        return roleNames[0];
      }
      return 'No roles';
    }
    
    const roleNames = user.UserRoles.map(ur => ur.Role).filter(name => name).filter((roleName: string) => roleName && !SYSTEM_ROLES.includes(roleName));
    return roleNames.length > 0 ? roleNames[0] : 'Unknown role'; // Just first role for brevity
  }

  /**
   * Calculates remaining time for current session
   */
  private calculateTimeRemaining(session: SessionInfo | null): TimerState | null {
    if (!session) {
      return null;
    }

    const now = Date.now();
    const expirationMs = session.expirationTime.getTime();
    const remainingMs = Math.max(0, expirationMs - now);
    const totalMs = session.durationMinutes * 60 * 1000;

    return {
      remainingMs,
      totalMs
    };
  }

  /**
   * Formats remaining time as MM:SS
   */
  public formatTimeRemaining(timer: TimerState): string {
    const totalSeconds = Math.floor(timer.remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Calculates progress percentage for timer bar
   */
  public getProgressPercentage(timer: TimerState): number {
    if (timer.totalMs === 0) return 0;
    return Math.max(0, Math.min(100, (timer.remainingMs / timer.totalMs) * 100));
  }

  /**
   * Extends the current impersonation session
   */
  public async extendSession(): Promise<void> {
    if (this.isExtending) return;

    this.isExtending = true;
    this.cdr.markForCheck();

    try {
      const extensionTime = 30 * 60 * 1000; // 30 minutes in milliseconds
      const result = await this.impersonationService.extendSession(extensionTime);

      if (!result.success) {
        console.error('Failed to extend impersonation session:', result.message);
        // Could show user notification here
      }
    } catch (error) {
      console.error('Error extending impersonation session:', error);
    } finally {
      this.isExtending = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Ends the current impersonation session
   */
  public async endImpersonation(): Promise<void> {
    if (this.isEnding) return;

    this.isEnding = true;
    this.cdr.markForCheck();

    try {
      const result = await this.impersonationService.endCurrentSession('manual');
      
      if (!result.success) {
        console.error('Failed to end impersonation session:', result.message);
        // Could show user notification here
      }
    } catch (error) {
      console.error('Error ending impersonation session:', error);
    } finally {
      this.isEnding = false;
      this.cdr.markForCheck();
    }
  }
}